import React from 'react';
import { Link } from 'react-router-dom';
import { User, CreditCard, Package, Shield, LogOut, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const Account = () => {
  const { user, logout } = useAuth();

  const getTierColor = (tier) => {
    switch(tier) {
      case 'seeker': return 'bg-gray-600';
      case 'berean': return 'bg-amber-600';
      case 'scholar': return 'bg-blue-600';
      case 'church': return 'bg-purple-600';
      default: return 'bg-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-4xl">
          {/* Welcome Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Welcome back, {user?.name}!
            </h1>
            <p className="text-gray-600">Manage your account and subscriptions</p>
          </div>

          {/* Subscription Status Card */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Subscription Status
                  </CardTitle>
                  <CardDescription>Your current access level</CardDescription>
                </div>
                <Badge className={`${getTierColor(user?.subscription_tier)} text-white capitalize`}>
                  {user?.subscription_tier}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-2">Status:</p>
                  <Badge variant="outline" className="capitalize">
                    {user?.subscription_status}
                  </Badge>
                </div>
                
                {user?.subscription_tier === 'seeker' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                    <h3 className="font-semibold text-amber-900 mb-2">
                      🎯 Upgrade to Unlock More
                    </h3>
                    <p className="text-sm text-amber-800 mb-4">
                      Get unlimited GIT investigations, full mind map access, and more with Berean or higher tiers.
                    </p>
                    <Link to="/subscriptions">
                      <Button size="sm" className="bg-amber-600 hover:bg-amber-700">
                        View Upgrade Options
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Account Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium">{user?.email}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium">{user?.name}</p>
              </div>
              
              {user?.church_organization && (
                <div>
                  <p className="text-sm text-gray-600">Church/Organization</p>
                  <p className="font-medium">{user.church_organization}</p>
                </div>
              )}
              
              {user?.purpose && (
                <div>
                  <p className="text-sm text-gray-600">Purpose</p>
                  <p className="font-medium capitalize">{user.purpose.replace('-', ' ')}</p>
                </div>
              )}
              
              <div>
                <p className="text-sm text-gray-600">Member Since</p>
                <p className="font-medium">
                  {new Date(user?.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <Link to="/ebook">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <BookOpen className="h-5 w-5" />
                    Read Little Book 2
                  </CardTitle>
                  <CardDescription>
                    The King's Return: Interactive ebook reader
                  </CardDescription>
                </CardHeader>
              </Link>
            </Card>

            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <Link to="/subscriptions">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <CreditCard className="h-5 w-5" />
                    Manage Subscription
                  </CardTitle>
                  <CardDescription>
                    Upgrade, downgrade, or cancel your plan
                  </CardDescription>
                </CardHeader>
              </Link>
            </Card>

            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <Link to="/store">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Package className="h-5 w-5" />
                    Browse Store
                  </CardTitle>
                  <CardDescription>
                    Shop books, shofars, and apothecary
                  </CardDescription>
                </CardHeader>
              </Link>
            </Card>
          </div>

          {/* Logout */}
          <Card>
            <CardContent className="pt-6">
              <Button
                variant="outline"
                onClick={logout}
                className="w-full md:w-auto"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Account;
